import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../config/axios';

const CATEGORIES = [
  { value: 'premium-nikahnama', label: 'Premium Nikahnama' },
  { value: 'nikah-frame', label: 'Nikah Frame' },
  { value: 'nikah-pen', label: 'Nikah Pen' },
];

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    long_description: '',
    price: '',
    original_price: '',
    image: '',
    images: ['', '', ''],
    category: '',
    in_stock: true,
    is_best_seller: false,
    customization: false,
  });

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/shop_products.php');
      if (response.data.success) {
        setProducts(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      long_description: '',
      price: '',
      original_price: '',
      image: '',
      images: ['', '', ''],
      category: '',
      in_stock: true,
      is_best_seller: false,
      customization: false,
    });
    setEditingProduct(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleOpenEdit = (product) => {
    setEditingProduct(product);
    // Handle images - if product has images array, use it; pad to 3 items
    let imagesArray = ['', '', ''];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      imagesArray = [...product.images];
      // Pad to 3 items
      while (imagesArray.length < 3) {
        imagesArray.push('');
      }
      imagesArray = imagesArray.slice(0, 3); // Ensure max 3
    }

    setForm({
      name: product.name || '',
      description: product.description || '',
      long_description: product.long_description || '',
      price: product.price || '',
      original_price: product.original_price || '',
      image: product.image || '',
      images: imagesArray,
      category: product.category || '',
      in_stock: product.in_stock ? true : false,
      is_best_seller: product.is_best_seller ? true : false,
      customization: product.customization ? true : false,
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (!saving) {
      setOpenModal(false);
      resetForm();
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (index, value) => {
    setForm((prev) => {
      const newImages = [...prev.images];
      newImages[index] = value;
      return { ...prev, images: newImages };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.description || !form.price || !form.image || !form.category) {
      setError('Please fill all required fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Filter out empty image URLs from images array
      const validImages = form.images.filter(img => img && img.trim() !== '');

      const payload = {
        name: form.name,
        description: form.description,
        long_description: form.long_description || null,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        image: form.image, // Main image
        images: validImages, // Images array (always send as array, even if empty)
        category: form.category,
        in_stock: form.in_stock ? 1 : 0,
        is_best_seller: form.is_best_seller ? 1 : 0,
        customization: form.customization ? 1 : 0,
      };

      if (editingProduct) {
        // Update product
        await api.put('/shop_products.php', { id: editingProduct.id, ...payload });
      } else {
        // Create product
        await api.post('/shop_products.php', payload);
      }

      handleCloseModal();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await api.delete('/shop_products.php', {
        data: { id: productId },
        headers: { 'Content-Type': 'application/json' }
      });
      setDeleteConfirm(null);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete product');
      setDeleteConfirm(null);
    }
  };

  const formatPrice = (price) => {
    return `₹${parseFloat(price || 0).toLocaleString('en-IN')}`;
  };

  return (
    <>
      <Helmet>
        <title>Products - Ishqnama Art Admin</title>
      </Helmet>
      <Container maxWidth="xl" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 }, background: '#FAF9F6' }}>
        <Box sx={{ mb: 5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'flex-end', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
              color: "#FFFFFF !important",
              fontWeight: 900,
              textTransform: "none",
              borderRadius: '12px',
              px: 4,
              py: 1.5,
              fontSize: "0.9rem",
              boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(181, 29, 110, 0.35)',
              }
            }}
          >
            New Product
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.05)',
            overflow: 'hidden',
            background: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress size={32} thickness={5} sx={{ color: 'primary.main' }} />
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 8 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "text.secondary" }}>
                No products found. Start by adding your first masterpiece.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F9FAFB', py: 2.5 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F9FAFB', py: 2.5 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F9FAFB', py: 2.5 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F9FAFB', py: 2.5 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: '#F9FAFB', py: 2.5 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.01)" }, borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                      <TableCell sx={{ py: 2.5 }}>
                        <Stack direction="row" spacing={2.5} alignItems="center">
                          {product.image ? (
                            <Box
                              component="img"
                              src={product.image}
                              alt={product.name}
                              sx={{
                                width: 56,
                                height: 56,
                                objectFit: 'cover',
                                borderRadius: 3,
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                              }}
                              onError={(e) => {
                                e.target.src = '/placeholder-image.png';
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                bgcolor: 'grey.50',
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px dashed rgba(0,0,0,0.1)'
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem' }}>No Image</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.9rem', mb: 0.25 }}>
                              {product.name}
                            </Typography>
                            {product.is_best_seller && (
                              <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: '4px', bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#D97706', fontSize: '0.65rem', fontWeight: 800, textTransform: 'uppercase' }}>
                                Best Seller
                              </Box>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'capitalize' }}>
                          {CATEGORIES.find((c) => c.value === product.category)?.label || product.category}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 900, color: 'text.primary', fontSize: '1rem' }}>
                          {formatPrice(product.price)}
                        </Typography>
                        {product.original_price && product.original_price > product.price && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 600, textDecoration: 'line-through', opacity: 0.6 }}
                          >
                            {formatPrice(product.original_price)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            bgcolor: product.in_stock ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: product.in_stock ? '#059669' : '#DC2626'
                          }}
                        >
                          {product.in_stock ? 'In Stock' : 'Sold Out'}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(product)}
                            sx={{
                              color: 'primary.main',
                              background: 'rgba(181, 29, 110, 0.05)',
                              '&:hover': { background: 'rgba(181, 29, 110, 0.1)' },
                              borderRadius: '10px'
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(product.id)}
                            sx={{
                              color: '#EF4444',
                              background: 'rgba(239, 68, 68, 0.05)',
                              '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                              borderRadius: '10px'
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Add/Edit Product Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: { borderRadius: 4, px: { xs: 1, sm: 2 }, py: 1 }
          }}
        >
          <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.75rem", color: "text.primary", pb: 1 }}>
            {editingProduct ? 'Edit Product' : 'New Creation'}
          </DialogTitle>
          <DialogContent sx={{ pt: '10px !important' }}>
            <Box component="form" id="product-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3 
              }}>
                <TextField
                  label="Product Name"
                  fullWidth
                  value={form.name}
                  onChange={handleChange('name')}
                  required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                />
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={form.category}
                    onChange={handleChange('category')}
                    sx={{ borderRadius: '12px' }}
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Sale Price (₹)"
                  fullWidth
                  type="number"
                  value={form.price}
                  onChange={handleChange('price')}
                  required
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                />
                <TextField
                  label="Original Price (₹)"
                  fullWidth
                  type="number"
                  value={form.original_price}
                  onChange={handleChange('original_price')}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                />

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <TextField
                    label="Short Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={form.description}
                    onChange={handleChange('description')}
                    required
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <TextField
                    label="Long Description (Detailed)"
                    fullWidth
                    multiline
                    rows={4}
                    value={form.long_description}
                    onChange={handleChange('long_description')}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                  />
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.01)', border: '1px dashed rgba(0,0,0,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', mb: 2, letterSpacing: '0.05em' }}>Product Flags</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                      <FormControlLabel
                        control={<Switch checked={form.in_stock} onChange={handleChange('in_stock')} color="primary" />}
                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>In Stock</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={form.is_best_seller} onChange={handleChange('is_best_seller')} color="warning" />}
                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Best Seller</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={form.customization} onChange={handleChange('customization')} color="indigo" />}
                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Customizable</Typography>}
                      />
                    </Box>
                  </Paper>
                </Box>

                <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1, letterSpacing: '0.05em' }}>Gallery</Typography>
                  <TextField
                    label="Main Thumbnail URL"
                    fullWidth
                    placeholder="https://..."
                    value={form.image}
                    onChange={handleChange('image')}
                    required
                    sx={{ mb: 3, "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                    {[0, 1, 2].map((idx) => (
                      <TextField
                        key={idx}
                        label={`Extra Image ${idx + 1}`}
                        fullWidth
                        value={form.images[idx] || ''}
                        onChange={(e) => handleImageChange(idx, e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 4, gap: 1.5 }}>
            <Button onClick={handleCloseModal} disabled={saving} sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-form"
              variant="contained"
              disabled={saving}
              sx={{
                background: '#B51D6E',
                color: "#FFFFFF !important",
                fontWeight: 900,
                textTransform: "none",
                borderRadius: '12px',
                px: 5,
                py: 1.5,
                boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
                '&:hover': {
                  background: '#D81B60',
                  boxShadow: '0 12px 35px rgba(181, 29, 110, 0.35)',
                }
              }}
            >
              {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'List Product'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this product? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              onClick={() => handleDelete(deleteConfirm)}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default Products;

